import { Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Checkbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import {
  isBeneficiaryDesignationEntity,
  isBeneficiaryDesignationEntityKind,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import {
  BUSINESS_ENTITY_KINDS,
  NON_TRUST_CHARITABLE_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import { EntityType } from '@/modules/entities/types/EntityType';
import { EntityKind } from '@/types/schema';

import { useCanEditDispositiveProvisions } from '../../authentication/hooks/useCanEditDispositiveProvisions';
import { useHideNoBeneficiaryDesignationsBadge } from './hooks/useHideNoBeneficiaryDesignationsBadge';
import { useHideNoDispositionsBadge } from './hooks/useHideNoDispositionsBadge';

interface DispositionScenario {
  id: string;
  /** When the scenario was reviewed. If falsy, the scenario has AI recommendations that haven't been reviewed. */
  reviewedAt?: Date | null;
}

/**
 * Minimal shape of the BDG resolver's `beneficiaryDesignations` field needed to
 * decide whether a retirement / insurance account has any designations set.
 */
export interface BeneficiaryDesignationsForBadge {
  primary?: { dispositiveProvisions: { totalCount: number } } | null;
  contingent?: { dispositiveProvisions: { totalCount: number } } | null;
}

export interface NoDispositionsBadgeProps {
  entityName: string;
  entityKind: EntityKind;
  /** The disposition scenarios for the entity. Badge shows when this is empty, undefined, or all scenarios have unreviewed AI recommendations. */
  dispositionScenarios: DispositionScenario[] | null | undefined;
  /**
   * BDG designations for retirement / insurance accounts. When the BDG flag is
   * on for these entities, the badge's empty state is driven by these counts
   * instead of {@link dispositionScenarios}.
   */
  beneficiaryDesignations?: BeneficiaryDesignationsForBadge | null;
  /**
   * Account-level acknowledgment timestamp for retirement / insurance entities.
   * When set, suppresses the BDG-driven badge even if no designations exist.
   */
  beneficiaryDesignationsReviewedAt?: Date | null;
  /** Callback to handle opening the dispositions editor */
  onAddDispositions: () => void;
  /** Entity ID for the hide badge mutation. Required for hide functionality. */
  entityId?: string;
  /** Entity subtype ID for the hide badge mutation. Required for hide functionality. */
  entitySubtypeId?: string;
  /** Entity type for building the mutation input. Required for hide functionality. */
  entityType?: EntityType;
}

/**
 * Entity kinds that should NOT show the No dispositions badge.
 * Includes DAFs and Private Foundations.
 */
const EXCLUDED_ENTITY_KINDS: Readonly<EntityKind[]> = [
  ...NON_TRUST_CHARITABLE_ENTITY_KINDS,
];

/**
 * Determines if the No dispositions badge should be shown for an entity.
 * Returns true if:
 * - The entity kind is not excluded (not a DAF or Private Foundation)
 * - AND either:
 *   - The entity has no disposition scenarios, OR
 *   - All disposition scenarios have AI recommendations that haven't been reviewed (reviewedAt is falsy)
 */
export function shouldShowNoDispositionsBadge(
  entityKind: EntityKind,
  dispositionScenarios: DispositionScenario[] | null | undefined
): boolean {
  const isExcludedKind = EXCLUDED_ENTITY_KINDS.includes(entityKind);
  const hasNoDispositions =
    !dispositionScenarios || dispositionScenarios.length === 0;
  // Also show badge if there are AI recommendations that haven't been reviewed
  const hasUnreviewedRecommendationsOnly =
    ((dispositionScenarios?.length ?? 0) > 0 &&
      dispositionScenarios?.every((scenario) => !scenario.reviewedAt)) ??
    false;
  return (
    !isExcludedKind && (hasNoDispositions || hasUnreviewedRecommendationsOnly)
  );
}

/**
 * BDG equivalent of {@link shouldShowNoDispositionsBadge}. The badge shows when
 * the entity has no primary or contingent designations AND the account-level
 * `beneficiaryDesignationsReviewedAt` hasn't been stamped (the BDG analog of
 * `DispositionScenario.reviewedAt`).
 */
export function shouldShowNoBeneficiaryDesignationsBadge(
  entityKind: EntityKind,
  beneficiaryDesignations: BeneficiaryDesignationsForBadge | null | undefined,
  beneficiaryDesignationsReviewedAt: Date | null | undefined
): boolean {
  const isExcludedKind = EXCLUDED_ENTITY_KINDS.includes(entityKind);
  const primaryCount =
    beneficiaryDesignations?.primary?.dispositiveProvisions.totalCount ?? 0;
  const contingentCount =
    beneficiaryDesignations?.contingent?.dispositiveProvisions.totalCount ?? 0;
  const hasNoDesignations = primaryCount + contingentCount === 0;
  return (
    !isExcludedKind && hasNoDesignations && !beneficiaryDesignationsReviewedAt
  );
}

/**
 * A badge component that displays "No dispositions" with a tooltip
 * containing a description and button to add dispositions.
 *
 * The badge will not render if:
 * - The entity kind doesn't require dispositions (DAFs, Private Foundations)
 * - The entity already has disposition scenarios
 */
export function NoDispositionsBadge({
  entityName,
  entityKind,
  dispositionScenarios,
  beneficiaryDesignations,
  beneficiaryDesignationsReviewedAt,
  onAddDispositions,
  entityId,
  entitySubtypeId,
  entityType,
}: NoDispositionsBadgeProps) {
  const canEdit = useCanEditDispositiveProvisions();

  // Retirement / DH-insurance accounts source their empty state from the BDG
  // resolver instead of the vestigial disposition scenarios.
  const readFromBeneficiaryDesignations =
    isBeneficiaryDesignationEntity(entityKind);

  // Business entities are excluded because useHideNoDispositionsBadge's
  // buildEntityInput throws for business entity types (see T1-5738).
  const canHideBadge =
    entityId &&
    entitySubtypeId &&
    entityType &&
    !BUSINESS_ENTITY_KINDS.includes(entityKind);

  const { hideNoDispositionsBadge, loading: hideLegacyBadgeLoading } =
    useHideNoDispositionsBadge({
      entityId: entityId ?? '',
      entitySubtypeId: entitySubtypeId ?? '',
      entityType: entityType ?? 'revocable-trust', // Default won't be used since we check canHideBadge
      dispositionScenarios,
    });

  const { hideNoBeneficiaryDesignationsBadge, loading: hideBdgBadgeLoading } =
    useHideNoBeneficiaryDesignationsBadge({
      entityId: entityId ?? '',
      entitySubtypeId: entitySubtypeId ?? '',
      entityType: entityType ?? 'retirement-account', // Default won't be used since we check canHideBadge
    });

  const hideBadgeLoading = readFromBeneficiaryDesignations
    ? hideBdgBadgeLoading
    : hideLegacyBadgeLoading;

  const [isHideBadgeChecked, setIsHideBadgeChecked] = useState(false);

  const handleAddDispositions = useCallback(
    (event: React.MouseEvent) => {
      // Stop propagation to prevent triggering parent click handlers (e.g., accordion, card navigation)
      event.stopPropagation();
      onAddDispositions();
    },
    [onAddDispositions]
  );

  const handleHideBadge = useCallback(() => {
    setIsHideBadgeChecked(true);
    if (readFromBeneficiaryDesignations) {
      hideNoBeneficiaryDesignationsBadge();
    } else {
      hideNoDispositionsBadge();
    }
  }, [
    readFromBeneficiaryDesignations,
    hideNoBeneficiaryDesignationsBadge,
    hideNoDispositionsBadge,
  ]);

  const stopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  // Don't render if we shouldn't show the badge
  const shouldShow = readFromBeneficiaryDesignations
    ? shouldShowNoBeneficiaryDesignationsBadge(
        entityKind,
        beneficiaryDesignations,
        beneficiaryDesignationsReviewedAt
      )
    : shouldShowNoDispositionsBadge(entityKind, dispositionScenarios);
  if (!shouldShow) {
    return null;
  }

  const isBenDesignation = isBeneficiaryDesignationEntityKind(entityKind);
  const label = isBenDesignation ? 'beneficiaries' : 'dispositions';

  const tooltipContent = (
    <Stack spacing={1} padding={0.5} onClick={stopPropagation}>
      <Typography variant="body-xs">
        No {label} entered for {entityName}
      </Typography>
      {canEdit && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="primary"
            size="xs"
            onClick={handleAddDispositions}
            sx={{ width: 'fit-content' }}
          >
            {`Add ${label}`}
          </Button>
          {canHideBadge && (
            <Checkbox
              name="hideBadge"
              label="Hide badge"
              value={isHideBadgeChecked}
              disabled={hideBadgeLoading || isHideBadgeChecked}
              onChange={handleHideBadge}
            />
          )}
        </Stack>
      )}
    </Stack>
  );

  return (
    <Tooltip title={tooltipContent} placement="bottom">
      <Badge
        wrapperSx={{ display: 'flex' }}
        variant={BadgeVariants.SecondaryOutline}
        display={`No ${label}`}
      />
    </Tooltip>
  );
}
